// Image preloader - loads and caches HTMLImageElement instances
const imageCache = new Map();

/**
 * Loads a single image and caches it.
 * Returns a promise that resolves with the loaded Image element.
 */
export function loadImage(src) {
    if (!src) return Promise.resolve(null);

    if (imageCache.has(src)) {
        return Promise.resolve(imageCache.get(src));
    }

    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            imageCache.set(src, img);
            resolve(img);
        };
        img.onerror = () => {
            console.warn(`Failed to load avatar image: ${src}`);
            resolve(null);
        };
        img.src = src;
    });
}

/**
 * Preloads all layer images for a given character data object.
 * Extracts all unique image sources and loads them in parallel.
 */
export function preloadCharacter(characterData) {
    const sources = new Set();

    Object.keys(characterData).forEach(layerName => {
        const layerVariations = characterData[layerName];

        layerVariations.forEach(variation => {
            if (Array.isArray(variation)) {
                // Standard layer: array of sub-layers
                variation.forEach(subLayer => {
                    if (subLayer && subLayer.Layer) sources.add(subLayer.Layer);
                });
            } else if (variation && typeof variation === 'object') {
                // Face layer: special structure with Layer0, Layer1, Layer2
                if (variation.Layer0) sources.add(variation.Layer0);
                if (variation.Layer1) sources.add(variation.Layer1);
                if (Array.isArray(variation.Layer2)) {
                    variation.Layer2.forEach(src => {
                        if (src) sources.add(src);
                    });
                }
            }
        });
    });

    const promises = Array.from(sources).map(src => loadImage(src));
    return Promise.all(promises);
}

/**
 * Gets a cached image synchronously. Returns null if not yet loaded.
 */
export function getCachedImage(src) {
    return imageCache.get(src) || null;
}

/**
 * Clears all cached images.
 */
export function clearCache() {
    imageCache.clear();
}
